import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { Button } from '@leadsales/design-system/react';
import { useTranslation } from 'react-i18next';
import type { tConnectionActivationFailedProps } from './ConnectionActivationFailed.types';
import '../WhatsAppConnect.shared.styles.scss';

const DEFAULT_NAMESPACE = 'whatsappConnect/whatsappConnect';

type tActivationFailureCopy = {
  titleKey: string;
  titleFallback: string;
  subtitleKey: string;
  subtitleFallback: string;
};

const GENERIC_ACTIVATION_FAILURE_COPY: tActivationFailureCopy = {
  titleKey: 'activationFailed.title',
  titleFallback: 'Meta no activó tu número',
  subtitleKey: 'activationFailed.subtitle',
  subtitleFallback:
    'Tu número no quedó activo en WhatsApp Business API, así que sus mensajes no llegan a Leadsales. Vuelve a conectarlo para intentarlo de nuevo.',
};

// INT-1516 — copy per WBS `activation_reason`, the registry pattern of
// ConnectionFailedError. `session_phone_mismatch` is absent on purpose: it never
// reaches activation_failed (KTD1), and if it ever did, generic copy is safe.
const ACTIVATION_FAILURE_COPY_BY_REASON: Record<string, tActivationFailureCopy> = {
  cloud_api_account_missing: {
    titleKey: 'activationFailed.cloudApiAccountMissing.title',
    titleFallback: 'Meta no activó tu número',
    subtitleKey: 'activationFailed.cloudApiAccountMissing.subtitle',
    subtitleFallback:
      'Meta no terminó de dar de alta tu número en WhatsApp Business API, así que sus mensajes no llegan a Leadsales. Vuelve a conectarlo para intentarlo de nuevo.',
  },
  waba_not_visible: {
    titleKey: 'activationFailed.wabaNotVisible.title',
    titleFallback: 'Leadsales ya no tiene acceso a tu cuenta de WhatsApp Business',
    subtitleKey: 'activationFailed.wabaNotVisible.subtitle',
    subtitleFallback:
      'Meta ya no nos deja ver la cuenta de este número, así que sus mensajes no llegan a Leadsales. Vuelve a conectarla y, cuando Meta te lo pida, selecciona esa cuenta.',
  },
};

// Own properties only: the reason is an open set from the backend, and a value
// such as "constructor" must fall back instead of resolving to Object's member.
function resolveActivationFailureCopy(reason: string | null | undefined): tActivationFailureCopy {
  if (reason && Object.prototype.hasOwnProperty.call(ACTIVATION_FAILURE_COPY_BY_REASON, reason)) {
    return ACTIVATION_FAILURE_COPY_BY_REASON[reason];
  }
  return GENERIC_ACTIVATION_FAILURE_COPY;
}

/**
 * INT-1516 — the record exists, but Meta did not activate its number. Not a
 * popup failure: there is no Meta error code or step to explain, only the
 * verdict's reason, and the remedy is a reconnect, so it is the primary action
 * (KTD10).
 */
export default function ConnectionActivationFailed(props: tConnectionActivationFailedProps) {
  const { t } = useTranslation(props.translationNamespace ?? DEFAULT_NAMESPACE);
  const copy = resolveActivationFailureCopy(props.reason);

  return (
    <div className="flex flex-col flex-1">
      <div className="flex flex-col items-center justify-center gap-6 py-12 text-center flex-1">
        <ExclamationTriangleIcon className="w-16 h-16 text-warning" aria-hidden="true" />

        <div className="flex flex-col gap-2 max-w-lg">
          <h2 className="text-2xl font-semibold text-base-content">
            {t(copy.titleKey, copy.titleFallback)}
          </h2>
          <p className="text-base text-base-content/60">
            {t(copy.subtitleKey, copy.subtitleFallback)}
          </p>
        </div>
      </div>

      <div className="WhatsAppConnect__actions">
        <Button color="tertiary" size="md" type="button" onClick={props.onBack}>
          {t('footer.back', 'Regresar')}
        </Button>
        <div className="WhatsAppConnect__actionsRight">
          <Button color="primary" size="md" type="button" onClick={props.onReconnect}>
            {t('activationFailed.cta', 'Reconectar')}
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
